import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PROFILE } from "@/data/index";
import { springPresets } from "@/lib/motion";

/* @section: about-hero */
function AboutHero() {
  return (
    <section className="py-28 bg-background border-b border-border">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
          className="max-w-2xl"
        >
          <span className="text-xs text-primary tracking-[0.3em] uppercase mb-6 block">Profile</span>
          <h1
            className="text-4xl md:text-5xl font-semibold text-foreground leading-[1.3] mb-6"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            プロフィール
          </h1>
          <div className="w-12 h-px bg-primary mb-6" />
          <p className="text-muted-foreground text-lg leading-[1.9]">
            代表のプロフィールと、HR Progressが大切にしている哲学をご紹介します。
          </p>
        </motion.div>
      </div>
    </section>
  );
}

/* @section: about-profile */
function ProfileSection() {
  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-5 gap-14 items-start">
          {/* Profile Visual */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="md:col-span-2"
          >
            <div
              className="w-full rounded-sm overflow-hidden border border-border mb-6"
              style={{ maxWidth: 320 }}
            >
              <img
                src="https://skyagent-artifacts.skywork.ai/router/agent/2026-06-07/prod_agent_1cd9b7fb-02fc-4548-8a80-a2996530d3c0/ce7e2c69e16be4983cd6ba947f23fe2e_exif_bbbcff14db0b4beeabc8f0f4889e220f.jpg"
                alt="代表プロフィール"
                className="w-full h-auto object-cover"
                style={{ aspectRatio: "3/4", objectPosition: "top center" }}
              />
            </div>
            {/* Credentials */}
            <div className="flex flex-col gap-3">
              {PROFILE.credentials.map((c, i) => (
                <div key={i} className="flex items-start gap-4 py-3 border-b border-border last:border-0">
                  <span className="text-xs text-primary tracking-wider w-24 shrink-0 pt-0.5" style={{ letterSpacing: "0.06em" }}>
                    {c.label}
                  </span>
                  <span className="text-sm text-muted-foreground leading-relaxed">{c.detail}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Bio */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
            className="md:col-span-3"
          >
            <span className="text-xs text-muted-foreground tracking-wider mb-2 block" style={{ letterSpacing: "0.1em" }}>
              {PROFILE.subtitle}
            </span>
            <h2
              className="text-3xl md:text-4xl font-semibold text-foreground mb-4"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              {PROFILE.name}
            </h2>
            <div className="w-10 h-px bg-primary mb-6" />

            {/* Philosophy */}
            <div
              className="border-l-2 border-primary pl-5 mb-8"
            >
              <p
                className="text-lg font-medium text-foreground leading-[1.7]"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                「{PROFILE.philosophy}」
              </p>
            </div>

            {/* Bio text */}
            <div className="flex flex-col gap-5">
              {PROFILE.bio.map((para, i) => (
                <p key={i} className="text-muted-foreground leading-[1.9] text-base">
                  {para}
                </p>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* @section: about-approach */
function Approach() {
  const steps = [
    {
      num: "01",
      title: "強みの発見",
      desc: "資質診断とコーチングセッションを通じて、あなた固有の強みを深く掘り下げます。",
    },
    {
      num: "02",
      title: "理解と言語化",
      desc: "なぜそれが強みなのかを理解し、自分の言葉で表現できるようにします。",
    },
    {
      num: "03",
      title: "実践と統合",
      desc: "日々の業務や行動の中で強みを意識的に活用し、成果に繋げていきます。",
    },
  ];
  return (
    <section className="py-24 bg-muted/30 border-t border-border">
      <div className="container mx-auto px-6">
        <div className="text-center mb-14">
          <span className="text-xs text-primary tracking-[0.25em] uppercase mb-4 block">Approach</span>
          <h2
            className="text-3xl font-semibold text-foreground"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            アプローチの流れ
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springPresets.gentle, delay: i * 0.1 }}
              className="bg-background border border-border p-8 rounded-sm"
            >
              <div
                className="text-4xl font-bold text-primary/20 mb-4"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {step.num}
              </div>
              <h3
                className="text-lg font-semibold text-foreground mb-3"
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {step.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-[1.8]">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* @section: about-cta */
function AboutCTA() {
  return (
    <section className="py-20 bg-background border-t border-border">
      <div className="container mx-auto px-6 text-center">
        <h2
          className="text-2xl md:text-3xl font-semibold text-foreground mb-4"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          まずは話してみませんか。
        </h2>
        <p className="text-muted-foreground mb-8">初回ご相談は無料です。お気軽にどうぞ。</p>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground text-sm tracking-wider hover:opacity-90 transition-opacity rounded-sm"
          style={{ letterSpacing: "0.1em" }}
        >
          お問い合わせはこちら
          <ArrowRight size={15} />
        </Link>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <AboutHero />
      <ProfileSection />
      <Approach />
      <AboutCTA />
    </>
  );
}
